import { useEffect, useRef, useState } from "react";
import { BREATH_PATTERNS } from "@/lib/content";
import { Play, Pause, RotateCcw } from "lucide-react";

type PatternKey = keyof typeof BREATH_PATTERNS;

interface Props {
  pattern: PatternKey;
  cycles?: number;
  onComplete?: () => void;
  compact?: boolean;
}

// Detect reduced-motion once at mount; user pref pauses the visual pulsing
// but the timer itself continues to advance so the practice still works.
function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const handler = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return reduced;
}

export function BreathTimer({ pattern, cycles = 8, onComplete, compact = false }: Props) {
  const config = BREATH_PATTERNS[pattern];
  const reduced = usePrefersReducedMotion();
  const [running, setRunning] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [progress, setProgress] = useState(0); // 0..1 within current step
  const [cycle, setCycle] = useState(0);
  const [done, setDone] = useState(false);
  const rafRef = useRef<number | null>(null);
  const stepStartRef = useRef<number>(0);

  useEffect(() => {
    if (!running || done) return;
    const step = config.steps[stepIdx];
    const durationMs = step.seconds * 1000;
    stepStartRef.current = performance.now();

    const tick = (now: number) => {
      const elapsed = now - stepStartRef.current;
      const p = Math.min(1, elapsed / durationMs);
      setProgress(p);
      if (p >= 1) {
        const nextIdx = (stepIdx + 1) % config.steps.length;
        if (nextIdx === 0) {
          const nextCycle = cycle + 1;
          if (nextCycle >= cycles) {
            setRunning(false);
            setDone(true);
            onComplete?.();
            return;
          }
          setCycle(nextCycle);
        }
        setStepIdx(nextIdx);
        return;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [running, stepIdx, cycle, config.steps, cycles, done, onComplete]);

  const reset = () => {
    setRunning(false); setStepIdx(0); setProgress(0); setCycle(0); setDone(false);
  };

  const current = config.steps[stepIdx];
  const scaleRaw = current.phase === "Inhale" ? 0.6 + 0.4 * progress
    : current.phase === "Exhale" ? 1 - 0.4 * progress
    : current.phase === "Hold" ? (stepIdx === 1 ? 1 : 0.6)
    : 0.8;
  const scale = reduced ? 0.8 : scaleRaw;

  if (done) {
    return (
      <div
        className={`flex flex-col items-center gap-4 rounded-2xl border hairline bg-charcoal p-8 text-center ${compact ? "" : "py-16"}`}
        role="status"
        aria-live="polite"
      >
        <div className="halo h-24 w-24 rounded-full animate-halo" aria-hidden />
        <p className="font-serif text-2xl text-gold-luminous">Activation complete.</p>
        <p className="max-w-md text-sm text-muted-foreground">
          Carry the ordering forward. Do not perform it. Simply live from it.
        </p>
        <button
          onClick={reset}
          className="mt-2 inline-flex items-center gap-2 rounded-full border hairline px-4 py-2 text-sm text-gold hover:bg-gold hover:text-obsidian focus:outline-none focus:ring-2 focus:ring-gold"
        >
          <RotateCcw size={16} /> Begin again
        </button>
      </div>
    );
  }

  return (
    <div
      className={`flex flex-col items-center gap-6 rounded-2xl border hairline bg-charcoal ${compact ? "p-6" : "p-8 sm:p-12"}`}
      role="group"
      aria-label={`Breath practice · ${config.label}`}
    >
      <div className="relative flex items-center justify-center" style={{ width: compact ? 140 : 220, height: compact ? 140 : 220 }}>
        <div
          className="absolute inset-0 rounded-full halo transition-transform duration-300 ease-out"
          style={{ transform: `scale(${scale})` }}
          aria-hidden
        />
        <div
          className="absolute inset-6 rounded-full border-2 border-gold transition-transform duration-300 ease-out shadow-halo"
          style={{ transform: `scale(${scale})` }}
          aria-hidden
        />
        <div className="relative text-center" aria-live="polite" aria-atomic>
          <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">{current.phase}</p>
          <p className={`font-serif ${compact ? "text-3xl" : "text-5xl"} text-gold-luminous`}>
            {Math.max(1, Math.ceil((1 - progress) * current.seconds))}
          </p>
        </div>
      </div>
      <p className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
        {config.label} · cycle {cycle + 1} of {cycles}
      </p>
      <div className="flex items-center gap-3">
        <button
          onClick={() => setRunning((r) => !r)}
          className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-gold-gradient text-obsidian shadow-gold hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-gold focus:ring-offset-2 focus:ring-offset-charcoal"
          aria-label={running ? "Pause activation" : "Start activation"}
          aria-pressed={running}
        >
          {running ? <Pause size={18} /> : <Play size={18} />}
        </button>
        <button
          onClick={reset}
          className="inline-flex h-12 w-12 items-center justify-center rounded-full border hairline text-gold hover:bg-gold hover:text-obsidian focus:outline-none focus:ring-2 focus:ring-gold"
          aria-label="Reset activation"
        >
          <RotateCcw size={16} />
        </button>
      </div>
      {reduced && (
        <p className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">Reduced motion · pulsing paused</p>
      )}
    </div>
  );
}
